import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import TrendingDownOutlinedIcon from '@mui/icons-material/TrendingDownOutlined';
import TrendingFlatOutlinedIcon from '@mui/icons-material/TrendingFlatOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SyncIcon from '@mui/icons-material/Sync';
import BedtimeOutlinedIcon from '@mui/icons-material/BedtimeOutlined';
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined';
import { useTranslation } from 'react-i18next';

import useGetPaymentSummary from './useGetPaymentSummary';

import { CONTEXT, PAYMENT_CARD_TYPES } from '../../../config/consts/commons';
import { formatCurrency } from '../../../utils';
import { COUNTRIES } from '@wareclouds/frontend-common';

const getTrendIcon = {
  up: <TrendingUpOutlinedIcon color="success" />,
  down: <TrendingDownOutlinedIcon color="error" />,
  equal: <TrendingFlatOutlinedIcon color="error" />,
};

export default function useGetDashboardData({ period, serviceName }) {
  const { t } = useTranslation(['description']);

  const { data, isLoading, isFetching, isError, status } = useGetPaymentSummary(
    {
      service: serviceName,
      endDate: period?.value,
    }
  );

  const isLoadingData = isLoading || isFetching || status === 'pending';

  if (!data) {
    return {
      dashboardData: [],
      isLoadingData,
      isErrorData: isError,
      bonus: null,
    };
  }

  const customerRatingCurrent = data?.metrics.customerRating.current;
  const customerRatingPrevious = data?.metrics.customerRating.previous;
  const hasCustomerRating = customerRatingCurrent > 0;
  const noCustomerRatingLabel = t(
    'payments.dashboard.performance.no_evaluation'
  );
  const currentCategoryEmoji =
    data?.classification.current?.emoji === '-'
      ? '🚗'
      : data?.classification.current?.emoji;

  const dashboardData = [
    {
      avatar: <AttachMoneyIcon color="secondary" />,
      title: t('payments.dashboard.metrics.total_earnings'),
      icon: null,
      value: formatCurrency({
        amount: data?.metrics.totalPayment.current,
      }),
      helperText: null,
      footerTitle: t('payments.dashboard.previous_period'),
      footerDescription: formatCurrency({
        amount: data?.metrics.totalPayment.previous,
      }),
      footerIcon: getTrendIcon[data?.metrics.totalPayment.trend],
      type: PAYMENT_CARD_TYPES.METRIC,
      show: true,
    },
    {
      avatar: <AccessTimeIcon color="secondary" />,
      title: t('payments.dashboard.metrics.on_time_deliveries'),
      icon: null,
      value: `${data?.metrics.onTimeDeliveries.current}%`,
      helperText: null,
      footerTitle: t('payments.dashboard.previous_period'),
      footerDescription: `${data?.metrics.onTimeDeliveries.previous}%`,
      footerIcon: getTrendIcon[data?.metrics.onTimeDeliveries.trend],
      type: PAYMENT_CARD_TYPES.METRIC,
      show: true,
    },
    {
      avatar: <HowToRegOutlinedIcon color="secondary" />,
      title: t('payments.dashboard.metrics.your_category'),
      icon: currentCategoryEmoji || null,
      value: data?.classification.current?.label || '-',
      helperText: null,
      footerTitle: null,
      footerDescription: null,
      footerIcon: null,
      type: PAYMENT_CARD_TYPES.METRIC,
      show: true,
    },
    {
      avatar: <DirectionsCarOutlinedIcon color="secondary" />,
      title: t('payments.dashboard.performance.routes_completed'),
      icon: getTrendIcon[data?.metrics.routesCompleted.trend],
      value: data?.metrics.routesCompleted.current,
      helperText: null,
      footerTitle: t('payments.dashboard.previous_period'),
      footerDescription: data?.metrics.routesCompleted.previous,
      type: PAYMENT_CARD_TYPES.PERFORMANCE,
      show: true,
    },
    {
      avatar: <ErrorOutlineIcon color="secondary" />,
      title: t('payments.dashboard.performance.protested_deliveries'),
      icon: getTrendIcon[data?.metrics.protestedDeliveries.trend],
      value: data?.metrics.protestedDeliveries.current,
      helperText: null,
      footerTitle: t('payments.dashboard.previous_period'),
      footerDescription: data?.metrics.protestedDeliveries.previous,
      type: PAYMENT_CARD_TYPES.PERFORMANCE,
      show: true,
    },
    {
      avatar: <SyncIcon color="secondary" />,
      title: t('payments.dashboard.performance.returns'),
      icon: getTrendIcon[data?.metrics.returns.trend],
      value: data?.metrics.returns.current,
      helperText: null,
      footerTitle: t('payments.dashboard.previous_period'),
      footerDescription: data?.metrics.returns.previous,
      type: PAYMENT_CARD_TYPES.PERFORMANCE,
      show: true,
    },
    {
      avatar: <BedtimeOutlinedIcon color="secondary" />,
      title: t('payments.dashboard.performance.delayed_returns'),
      icon: getTrendIcon[data?.metrics.delayedReturns.trend],
      value: data?.metrics.delayedReturns.current,
      helperText: t('payments.dashboard.performance.delayed_returns_helper'),
      footerTitle: t('payments.dashboard.previous_period'),
      footerDescription: data?.metrics.delayedReturns.previous,
      type: PAYMENT_CARD_TYPES.PERFORMANCE,
      show: true,
    },
    {
      avatar: <StarBorderOutlinedIcon color="secondary" />,
      title: t('payments.dashboard.performance.customer_rating'),
      icon: hasCustomerRating
        ? getTrendIcon[data?.metrics.customerRating.trend]
        : null,
      value: hasCustomerRating
        ? `${customerRatingCurrent} ⭐️`
        : noCustomerRatingLabel,
      valueMuted: !hasCustomerRating,
      helperText: null,
      footerTitle: t('payments.dashboard.previous_period'),
      footerDescription:
        customerRatingPrevious > 0 ? customerRatingPrevious : '-',
      type: PAYMENT_CARD_TYPES.PERFORMANCE,
      show: true,
    },
    {
      avatar: <AttachMoneyIcon color="secondary" />,
      title: t('payments.dashboard.details.routes'),
      icon: getTrendIcon[data?.earnings.routes.trend],
      value: formatCurrency({ amount: data?.earnings.routes.current }),
      helperText: null,
      footerTitle: t('payments.dashboard.previous_period'),
      footerDescription: formatCurrency({
        amount: data?.earnings.routes.previous,
      }),
      type: PAYMENT_CARD_TYPES.DETAILS,
      show: true,
    },
    {
      avatar: <AttachMoneyIcon color="secondary" />,
      title: t('payments.dashboard.details.performance_bonus'),
      icon: getTrendIcon[data?.earnings.performanceBonus.trend],
      value: formatCurrency({
        amount: data?.earnings.performanceBonus.current,
      }),
      helperText: null,
      footerTitle: t('payments.dashboard.previous_period'),
      footerDescription: formatCurrency({
        amount: data?.earnings.performanceBonus.previous,
      }),
      type: PAYMENT_CARD_TYPES.DETAILS,
      show: true,
    },
    {
      avatar: <AttachMoneyIcon color="secondary" />,
      title: t('payments.dashboard.details.multiple_packages'),
      icon: getTrendIcon[data?.earnings.multiplePackages.trend],
      value: data?.earnings.multiplePackages.current,
      helperText: null,
      footerTitle: t('payments.dashboard.previous_period'),
      footerDescription: data?.earnings.multiplePackages.previous,
      type: PAYMENT_CARD_TYPES.DETAILS,
      show: CONTEXT === COUNTRIES.CL,
    },
    {
      avatar: <AttachMoneyIcon color="secondary" />,
      title: t('payments.dashboard.details.large_packages'),
      icon: getTrendIcon[data?.earnings.largePackages.trend],
      value: data?.earnings.largePackages.current,
      helperText: null,
      footerTitle: t('payments.dashboard.previous_period'),
      footerDescription: data?.earnings.largePackages.previous,
      type: PAYMENT_CARD_TYPES.DETAILS,
      show: CONTEXT === COUNTRIES.CL,
    },
    {
      avatar: <AttachMoneyIcon color="secondary" />,
      title: t('payments.dashboard.details.settlements'),
      icon: getTrendIcon[data?.earnings.settlements.trend],
      value: formatCurrency({ amount: data?.earnings.settlements.current }),
      helperText: null,
      footerTitle: t('payments.dashboard.previous_period'),
      footerDescription: formatCurrency({
        amount: data?.earnings.settlements.previous,
      }),
      type: PAYMENT_CARD_TYPES.DETAILS,
      show: true,
    },
  ];

  return {
    dashboardData,
    isLoadingData,
    isErrorData: isError,
    bonus: data?.earnings.firstWeekBonus,
  };
}
